import UnitConverter, { Units } from "../../core/unitConverter";
import BodyMassIndex from "../../HealthCalculators/BodyMassIndex";
import BodyMassIndexGuideline from "../BodyMassIndex/SiSU";

const KG_DECIMAL_PLACES = 1;

/**
 * Round DOWN to 0.1kg for the upper bound. PR #419 established that BMI classification runs on
 * the 1dp-rounded (half-up) value, not the raw one — see `BodyMassIndex/SiSU._displayBmi` and
 * `isNormal()`, and `Weight/SiSU._BMI_ROUNDING_OFFSET` (0.05), which exists precisely because
 * the effective normal/elevated boundary is `T - 0.05`, not `T`. There is therefore only 0.05
 * BMI of headroom above the displayed upper bound (24.9 standard / 22.9 adjusted), not the 0.1
 * a naive reading of the display step suggests. Half-up rounding can consume that headroom
 * (confirmed live: 150cm standard imperial upper rounds half-up to 124lb = BMI 24.998, which
 * round1dp's to 25.0 and classifies Elevated — the exact "displayed number disagrees with its
 * own band" bug PR #419 fixed elsewhere). Rounding DOWN is step-size independent and needs no
 * per-height margin analysis: it guarantees the raw BMI at the displayed weight is <= the exact
 * threshold, so `round1dp(bmi) <= threshold < threshold + 0.05` always holds, classifying
 * Normal at every height. A tiny epsilon avoids nudging a value that is already (up to
 * floating-point noise) an exact multiple of 0.1 down an extra needless step.
 *
 * @param {number} weightInKg
 * @returns {number}
 */
const roundDownToDisplayStep = weightInKg => Math.floor(weightInKg * 10 + 1e-9) / 10;

/**
 * Round UP to 0.1kg for the lower bound specifically. The lower bound sits exactly ON the
 * band's edge BMI (e.g. 18.5) with no slack either side, so plain half-up rounding — and
 * definitely Math.floor — is not safe: e.g. at 150cm the lower-bound weight is 41.625kg, which
 * rounds to 41.6kg under ordinary round-half-up (the digit after the first decimal is 2, not
 * >= 5) — that is BMI 18.4889, outside the band it's meant to define. Flooring is even worse
 * (18.5 x 1.70^2 = 53.465kg floored to 53kg is BMI 18.34). Rounding up unconditionally
 * guarantees the displayed weight's BMI is always >= the threshold. A tiny epsilon avoids
 * nudging values that are already (up to floating-point noise) an exact multiple of 0.1 up an
 * extra step.
 *
 * @param {number} weightInKg
 * @returns {number}
 */
const roundUpToDisplayStep = weightInKg => Math.ceil(weightInKg * 10 - 1e-9) / 10;

/**
 * Round UP to the next whole pound. `UnitConverter.kgToStLbsString` rounds to the nearest
 * whole pound (half up) via `stLbsToStLbsString`'s internal `Math.round`, which reintroduces
 * the exact failure mode `roundUpToDisplayStep` exists to prevent, just at a coarser step: a
 * pound is ~0.454kg, so ordinary half-up rounding can move the lower bound up to ~0.227kg
 * downward — 4.5x the ~0.05kg slack the metric analysis tolerates, comfortably enough to push
 * an edge-of-band lower bound (e.g. 200cm standard: 74.0kg = 163.14lb -> "11st 9lb" = 163lb =
 * 73.94kg = BMI 18.485, underweight) outside the band. We cannot fix this inside
 * `UnitConverter.kgToStLbsString` (other callers rely on its half-up behaviour), so the
 * pound-level rounding is done here instead, directly on a pounds value — not chained through
 * the 0.1kg metric rounding above, to avoid compounding two roundings.
 *
 * @param {number} weightInPounds
 * @returns {number} whole pounds, rounded up
 */
const roundUpToWholePound = weightInPounds => Math.ceil(weightInPounds - 1e-9);

/**
 * Round DOWN to the next whole pound for the upper bound. Same reasoning as
 * `roundDownToDisplayStep`: PR #419 means the effective boundary is `T - 0.05` BMI, not `T`, so
 * there is no fixed margin a half-up rounding step is guaranteed to fit inside — confirmed live
 * at 150/160/170cm standard, where half-up rounding to a whole pound pushed the displayed upper
 * bound's BMI past 24.95 and it classified Elevated. Flooring guarantees the displayed weight's
 * raw BMI never exceeds the exact threshold, independent of step size.
 *
 * @param {number} weightInPounds
 * @returns {number} whole pounds, rounded down
 */
const roundDownToWholePound = weightInPounds => Math.floor(weightInPounds + 1e-9);

/**
 * Rounds an already-computed healthy-weight band to its display precision, applying the
 * rule this module exists for: round INWARD at both ends — the lower bound rounds up/away from
 * the band, the upper bound rounds down/away from the band. This is required (not just safer)
 * because of PR #419: classification runs on the 1dp-rounded BMI, so the true boundary sits at
 * `threshold - 0.05`, not `threshold`. Rounding inward guarantees the displayed weight's raw
 * BMI never crosses the exact threshold in either direction, so it always classifies Normal
 * regardless of step size (0.1kg or a whole pound) or height — no per-height margin analysis
 * needed. See the rounder doc comments above for the confirmed live breaches this replaced.
 *
 * This is the single source of truth for "how do target weight bounds round". Any caller that
 * needs the band as numbers rather than as a display string (e.g. the trend-chart tooltip,
 * which must keep `target` numeric for its positioning maths) must round through this rather
 * than re-implementing the rule.
 *
 * @param {number} lowerWeight - raw/unrounded lower bound, in kg if !isImperial else in lbs
 * @param {number} upperWeight - raw/unrounded upper bound, in kg if !isImperial else in lbs
 * @param {boolean} isImperial
 * @returns {{lower: number, upper: number}} rounded numbers, same unit as the input
 */
export function roundTargetWeightBounds(lowerWeight, upperWeight, isImperial = false) {
    return isImperial
        ? { lower: roundUpToWholePound(lowerWeight), upper: roundDownToWholePound(upperWeight) }
        : { lower: roundUpToDisplayStep(lowerWeight), upper: roundDownToDisplayStep(upperWeight) };
}

/**
 * The healthy weight band as display-ready strings, including the unit. The recommendation
 * copy carries no literal unit ("a weight between {lower} and {upper}"), so the unit has to
 * travel inside the value — that is what lets one set of translation keys serve both
 * measurement systems.
 *
 * @param {number|null} heightCm
 * @param {string|null} ethnicity - resolved NHS/IRE ethnicity value or fallback question code
 * @param {boolean} isImperial
 * @returns {{lower: string, upper: string}}
 */
export default function formatTargetWeightRange(heightCm, ethnicity, isImperial = false) {
    if (!heightCm) return { lower: "", upper: "" };

    const { lower, upper } = BodyMassIndexGuideline.healthyBmiRangeForEthnicity(ethnicity);

    const weightInUnit = bmi => {
        const weightInKg = BodyMassIndex.getWeightForHeightAndBMI(heightCm, bmi);
        return isImperial ? UnitConverter.kgToPounds(weightInKg) : weightInKg;
    };

    const rounded = roundTargetWeightBounds(weightInUnit(lower), weightInUnit(upper), isImperial);

    const toDisplay = weight =>
        isImperial
            ? UnitConverter.poundsToStLbsString(weight)
            : UnitConverter.toString(weight, Units.metric.weight.kilogram, KG_DECIMAL_PLACES);

    return { lower: toDisplay(rounded.lower), upper: toDisplay(rounded.upper) };
}
